import React, { useState } from "react"
import BootstrapTable from "react-bootstrap-table-next"
import { customSortCaret } from "helpers/bootstrap_table_helper"
import { forColumnHeaderWrapper } from "helpers/sf_utils"
import { Button, Col, Row } from "reactstrap"
import {
  copyTextToClipboard,
  createInvestigationReport,
  failureDetailsForResult,
  groupFailureDetails,
  statusPresentation,
} from "./reportingConsistencyHelper"

const ReportingTable = ({ columns, data, keyField = "id", noDataIndication = "No data returned.", ...props }) => (
  <BootstrapTable
    keyField={keyField}
    data={data}
    columns={columns}
    responsive
    bordered={false}
    striped={false}
    rowClasses="tr-hover"
    headerClasses="sftableHeader thead-h50 text-black"
    classes="table align-middle table-nowrap text-black"
    headerWrapperClasses="thead-light"
    noDataIndication={noDataIndication}
    sort={{ sortCaret: customSortCaret }}
    {...props}
  />
)

const StatusCell = ({ status }) => {
  const presentation = statusPresentation(status)
  return (
    <React.Fragment>
      <i className={`bx ${presentation.icon} ${presentation.className} me-2`} />
      <span className={`fsnormal ${presentation.className}`}>{presentation.label}</span>
    </React.Fragment>
  )
}

const statusFormatter = cellContent => <StatusCell status={cellContent} />

const formatFailureValue = value => typeof value === "number" ? value.toLocaleString() : value

const FailureDetails = ({ result }) => {
  const failures = failureDetailsForResult(result)
  const failureGroups = groupFailureDetails(failures)
  const isApiError = result?.classification === "API_ERROR" || result?.error
  if (!failures.length) {
    return (
      <div className="alert alert-success mb-0">
        <StatusCell status="PASS" />
        <div className="mt-2">All checked reporting surfaces match for this account and timeframe.</div>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="alert alert-danger mb-3">
        <StatusCell status={result?.classification || "DATA_MISMATCH"} />
        <div className="mt-2">{isApiError ? "Review the failed API request below." : "Review the mismatched metrics below."}</div>
      </div>
      <div className="reporting-consistency__failure-list">
        {failureGroups.map(group => (
          <div className="reporting-consistency__failure" key={`${group.location}-${group.expectedLabel}-${group.actualLabel}`}>
            <div className="font-weight-bold mb-2">{group.location}</div>
            <div className="reporting-consistency__failure-summary">
              {group.values.map(failure => (
                <span key={`${group.location}-${failure.metric}`}>
                  <strong>{failure.metric.replace(/ mismatch$/, "")}:</strong> {group.expectedLabel} {formatFailureValue(failure.expected)} vs {group.actualLabel} {formatFailureValue(failure.actual)}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </React.Fragment>
  )
}

export const ReportingRunDetails = ({ result }) => {
  const [copyStatus, setCopyStatus] = useState("")
  const scope = result?.scope || {}
  const investigationReport = createInvestigationReport(result)
  const scopeRows = [
    ["Account", scope.account_id || "-"],
    ["Product", scope.product || "-"],
    ["Period", `${scope.start_date || "-"} to ${scope.end_date || "-"}`],
    ["Grouped by", (scope.group_by_params || []).join(", ") || "-"],
    ["Requests", result?.request_count ?? result?.collection?.request_count ?? "-"],
    ["Timezone", scope.timezone || "-"],
    ["Duration", result?.duration_ms != null ? `${result.duration_ms} ms` : "-"],
  ]
  return (
    <div className="reporting-consistency__run-details">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 className="mb-1">What this run checked</h5>
          <small className="text-muted">Review the summary first; technical JSON is available at the bottom.</small>
        </div>
        <StatusCell status={result?.classification || "ERROR"} />
      </div>
      <p className="text-muted mb-3">{result?.visible_summary || "The selected reporting surfaces were compared for the same account and date range."}</p>

      <RowSummary rows={scopeRows} />
      <FailureDetails result={result} />

      <div className="mt-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h6 className="mb-1">AI investigation report</h6>
            <small className="text-muted">Copy this compact report into an AI agent to investigate the root cause.</small>
          </div>
          <Button
            type="button"
            color="secondary"
            outline
            size="sm"
            onClick={async () => {
              setCopyStatus("Copying...")
              try {
                await copyTextToClipboard(investigationReport)
                setCopyStatus("Copied")
              } catch (error) {
                setCopyStatus("Copy failed; select the report manually")
              }
            }}
          >
            <i className={`bx ${copyStatus === "Copied" ? "bx-check" : "bx-copy"} me-1`} />
            {copyStatus || "Copy report"}
          </Button>
        </div>
        <pre className="reporting-consistency__investigation-report">{investigationReport}</pre>
      </div>

      <details className="mt-3">
        <summary>Technical details</summary>
        <pre className="mt-2 reporting-consistency__technical-details">{JSON.stringify(result, null, 2)}</pre>
      </details>
    </div>
  )
}

const RowSummary = ({ rows }) => (
  <Row className="reporting-consistency__summary mb-4">
    {rows.map(([label, value]) => (
      <Col key={label} md="4" className="mb-3">
        <div className="text-muted small">{label}</div>
        <div>{value}</div>
      </Col>
    ))}
  </Row>
)

export const ReportingChecksTable = ({ runs, expandedRunId, onRunClick }) => {
  const columns = [
    { dataField: "id", text: "Check", headerFormatter: forColumnHeaderWrapper, formatter: cellContent => `#${cellContent}` },
    { dataField: "status", text: "Status", headerFormatter: forColumnHeaderWrapper, formatter: statusFormatter },
    { dataField: "started_at", text: "Started", headerFormatter: forColumnHeaderWrapper, formatter: cellContent => cellContent ? new Date(cellContent).toLocaleString() : "-" },
    { dataField: "request_count", text: "Requests", headerFormatter: forColumnHeaderWrapper, formatter: cellContent => cellContent || "-" },
  ]
  return (
    <ReportingTable
      keyField="id"
      data={runs}
      columns={columns}
      rowEvents={{ onClick: (_event, row) => onRunClick(row.id) }}
      rowClasses={row => row.id === expandedRunId ? "tr-hover reporting-consistency__selected-row" : "tr-hover"}
      noDataIndication="No checks run in this session yet."
    />
  )
}

export const OverallStatus = ({ status }) => <StatusCell status={status} />
